import { z } from "zod";
import { IdPageQuery, IsoDateTime, Uuid, pageOf } from "./common";

export const READINESS_STATUSES = ["ok", "warn", "fail", "unknown"] as const;

export const ReadinessCheckDto = z.object({
  id: z.string(),
  group: z.string(),
  severity: z.enum(["critical", "warning", "info"]),
  status: z.enum(READINESS_STATUSES),
  detail: z.string(),
  fix: z.string().optional(),
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});

export const ReadinessReportDto = z.object({
  level: z.enum(["tenant", "host", "platform"]),
  generatedAt: IsoDateTime,
  ready: z.boolean(),
  summary: z.object({ ok: z.number(), warn: z.number(), fail: z.number(), unknown: z.number() }),
  checks: z.array(ReadinessCheckDto),
});
export type ReadinessReportDto = z.infer<typeof ReadinessReportDto>;

export const LIVE_KINDS = ["messaging", "social", "ads"] as const;
export const LiveKind = z.enum(LIVE_KINDS);
export type LiveKind = z.infer<typeof LiveKind>;

export const LiveSwitchState = z.object({
  kind: LiveKind,
  env: z.boolean(),
  host: z.boolean(),
  /** Null when the report is host-level, with no tenant addressed. */
  tenant: z.boolean().nullable(),
  effective: z.boolean(),
  blockedBy: z.enum(["env", "host", "tenant", "suspended"]).nullable(),
});
export type LiveSwitchState = z.infer<typeof LiveSwitchState>;

export const LiveSwitchesResponse = z.object({ switches: z.array(LiveSwitchState) });
export type LiveSwitchesResponse = z.infer<typeof LiveSwitchesResponse>;

/** `level: "host"` is accepted from host keys only. */
export const LiveSwitchUpdate = z
  .object({
    kind: LiveKind,
    enabled: z.boolean(),
    level: z.enum(["tenant", "host"]).default("tenant"),
  })
  .strict();
export type LiveSwitchUpdate = z.infer<typeof LiveSwitchUpdate>;

/**
 * `mcp` is a key for an AI assistant: it opens `/v1/mcp` and nothing else, on one tenant, and stands alone. What it can do
 * there is what its tools allow, and an approval through it is the agent's, never a person's.
 */
export const API_KEY_SCOPES = ["read", "write", "approve", "admin", "mcp"] as const;
export const ApiKeyScope = z.enum(API_KEY_SCOPES);
export type ApiKeyScope = z.infer<typeof ApiKeyScope>;

export const ApiKeyDto = z.object({
  id: Uuid,
  level: z.enum(["tenant", "host"]),
  tenantId: Uuid.nullable(),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(ApiKeyScope),
  lastUsedAt: IsoDateTime.nullable(),
  revokedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type ApiKeyDto = z.infer<typeof ApiKeyDto>;

export const ApiKeyCreateRequest = z
  .object({
    name: z.string().trim().min(1).max(120),
    scopes: z.array(ApiKeyScope).min(1),
    /** `host` is accepted from host keys with the `admin` scope only. */
    level: z.enum(["tenant", "host"]).default("tenant"),
  })
  .strict()
  .refine((key) => !key.scopes.includes("mcp") || (key.scopes.length === 1 && key.level === "tenant"), { message: "mcp_scope_stands_alone", path: ["scopes"] });
export type ApiKeyCreateRequest = z.infer<typeof ApiKeyCreateRequest>;

/** `token` appears here once and never again. */
export const ApiKeyCreated = ApiKeyDto.extend({ token: z.string() });
export type ApiKeyCreated = z.infer<typeof ApiKeyCreated>;

export const ApiKeyList = pageOf(ApiKeyDto);

export const AuditEntryDto = z.object({
  id: Uuid,
  tenantId: Uuid.nullable(),
  actorKind: z.string(),
  actorId: z.string(),
  actorName: z.string(),
  action: z.string(),
  entity: z.string(),
  entityId: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  result: z.string(),
  requestId: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AuditEntryDto = z.infer<typeof AuditEntryDto>;

export const AuditList = pageOf(AuditEntryDto);

export const AUDIT_RESULTS = ["ok", "denied", "failed", "dry_run"] as const;

/** `actor` matches part of the name, ignoring case. `from` is inclusive, `to` exclusive. */
export const AuditQuery = IdPageQuery.extend({
  action: z.string().trim().min(1).max(120).optional(),
  actor: z.string().trim().min(1).max(200).optional(),
  result: z.enum(AUDIT_RESULTS).optional(),
  from: IsoDateTime.optional(),
  to: IsoDateTime.optional(),
});
export type AuditQuery = z.infer<typeof AuditQuery>;

export const HealthResponse = z.object({ ok: z.literal(true), service: z.literal("imagora-api"), contract: z.string() });

/** Which service this process can't reach, and nothing else: the route is public. */
export const ReadyResponse = z.object({ ok: z.boolean(), failed: z.array(z.enum(["postgres_app", "postgres_system", "redis"])) });
